import { forwardRef } from "react";

import Slide from "@mui/material/Slide";
import Dialog from "@mui/material/Dialog";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { TransitionProps } from "@mui/material/transitions";
import Stack from "@mui/material/Stack";
import { useBoolean } from "src/hooks/use-boolean";
import { _applications } from "src/_mock";
import Iconify from "src/components/iconify";
import { t } from "i18next";

import {
  Tooltip,
  DialogContent,
  DialogTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import VideoPlayer from "src/components/videoplayer/videoplayer";
import { Trans } from "react-i18next";
// ----------------------------------------------------------------------

const Transition = forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
);
interface Section {
  key: string;
  title: string;
  content: string;
  video: string;
}
interface HelpDialogProps {
  section?: string;
}

export default function AdminHelpDialog({ section }: HelpDialogProps) {
  const dialog = useBoolean();
  const theme = useTheme();

  // Define your sections
  const sections: Section[] = [
    {
      key: "admin_help_section_admin_start_steps",
      title: "admin_help_section_admin_start_steps",
      content: "admin_help_section_admin_start_steps_content",
      video: "", //"/assets/videos/about/foggo_promo_1_2022.mp4",
    },
    {
      key: "admin_help_section_services",
      title: "admin_help_section_services",
      content: "admin_help_section_services_content",
      video: "",
    },
    {
      key: "admin_help_section_profile",
      title: "admin_help_section_profile",
      content: "admin_help_section_profile_content",
      video: "",
    },
    {
      key: "admin_help_section_images",
      title: "admin_help_section_images",
      content: "admin_help_section_images_content",
      video: "",
    },
    {
      key: "admin_help_section_sports",
      title: "admin_help_section_sports",
      content: "admin_help_section_sports_content",
      video: "",
    },
    {
      key: "admin_help_section_venues",
      title: "admin_help_section_venues",
      content: "admin_help_section_venues_content",
      video: "",
    },
    {
      key: "admin_help_section_billing_infos",
      title: "admin_help_section_billing_infos",
      content: "admin_help_section_billing_infos_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_global",
      title: "admin_help_section_settings_global",
      content: "admin_help_section_settings_global_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_pricing",
      title: "admin_help_section_settings_pricing",
      content: "admin_help_section_settings_pricing_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_reservations",
      title: "admin_help_section_settings_reservations",
      content: "admin_help_section_settings_reservations_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_trainings",
      title: "admin_help_section_settings_trainings",
      content: "admin_help_section_settings_trainings_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_blockings",
      title: "admin_help_section_settings_blockings",
      content: "admin_help_section_settings_blockings_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_passes",
      title: "admin_help_section_settings_passes",
      content: "admin_help_section_settings_passes_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_tickets",
      title: "admin_help_section_settings_tickets",
      content: "admin_help_section_settings_tickets_content",
      video: "",
    },
    {
      key: "admin_help_section_calendar",
      title: "admin_help_section_calendar",
      content: "admin_help_section_calendar_content",
      video: "",
    },
    {
      key: "admin_help_section_transactions",
      title: "admin_help_section_transactions",
      content: "admin_help_section_transactions_content",
      video: "",
    },
    {
      key: "admin_help_section_players",
      title: "admin_help_section_players",
      content: "admin_help_section_players_content",
      video: "",
    },
    {
      key: "admin_help_section_analytics",
      title: "admin_help_section_analytics",
      content: "admin_help_section_analytics_content",
      video: "",
    },
  ];

  // If a section prop is provided, reorder so that it appears first.
  const orderedSections = section
    ? [
        ...sections.filter((sec) => sec.key === section),
        ...sections.filter((sec) => sec.key !== section),
      ]
    : sections;

  return (
    <>
      <Tooltip title={t("help")}>
        <IconButton
          onClick={dialog.onTrue}
          sx={{
            paddingTop: 0.5,
            transform: "scale(1.3)", // Scale up the button (and its content)
            color: theme.palette.info.main,
          }}
        >
          <Iconify icon="mingcute:question-line" />
        </IconButton>
      </Tooltip>

      <Dialog
        fullScreen
        PaperProps={{
          sx: { maxWidth: 1000 },
        }}
        open={dialog.value}
        onClose={dialog.onFalse}
        TransitionComponent={Transition} // Ensure your Transition component is imported
      >
        <DialogTitle>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1, ml: 0 }}>
              {t("help")}
            </Typography>
            <IconButton color="inherit" edge="end" onClick={dialog.onFalse}>
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Toolbar>
        </DialogTitle>
        <DialogContent sx={{ overflowY: "auto" }}>
          <Stack spacing={2} mx={3} pb={3}>
            {orderedSections.map((sec) => (
              <Accordion
                key={sec.key}
                defaultExpanded={sec.key === section}
                sx={{
                  // When expanded, the root element of Accordion gets the "Mui-expanded" class.
                  // Here we override its background color to green.
                  "&.Mui-expanded": {
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <Iconify
                      icon="ph:caret-down-bold"
                      width={20}
                      sx={{ float: "left" }}
                    />
                  }
                >
                  <Typography variant="subtitle1">{t(sec.title)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction={"column"} spacing={2}>
                    <Stack>
                      {/* <Typography>{t(sec.content)}</Typography> */}
                      <Trans i18nKey={sec.content}></Trans>
                    </Stack>
                    {sec.video && (
                      <Stack alignItems={"center"}>
                        <VideoPlayer src={t(sec.video)} alt={t(sec.title)} />
                      </Stack>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
